import React from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Spinner } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { alertError, alertSuccess } from '../../../store/alerts/actions';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { TextInputField } from '../../../components/Forms/fields/TextInputField';
import { ComboField } from '../../../components/Forms/fields/ComboField';
import { WhatsAppTemplateCatalogItem, WhatsAppTemplateComponent } from '../../../models';
import { createWhatsAppTemplateAsync, updateWhatsAppTemplateAsync } from '../../../ajax/requests';
import { getErrorMessage } from '../../../ajax/errors';
import AppButton from '../../../components/AppButton';


const LANGUAGES = [
    { value: 'af', label: 'Afrikaans' },
    { value: 'sq', label: 'Albanian' },
    { value: 'ar', label: 'Arabic' },
    { value: 'az', label: 'Azerbaijani' },
    { value: 'bn', label: 'Bengali' },
    { value: 'bg', label: 'Bulgarian' },
    { value: 'ca', label: 'Catalan' },
    { value: 'zh_CN', label: 'Chinese (Simplified)' },
    { value: 'zh_TW', label: 'Chinese (Traditional)' },
    { value: 'hr', label: 'Croatian' },
    { value: 'cs', label: 'Czech' },
    { value: 'da', label: 'Danish' },
    { value: 'nl', label: 'Dutch' },
    { value: 'en', label: 'English' },
    { value: 'en_GB', label: 'English (UK)' },
    { value: 'en_US', label: 'English (US)' },
    { value: 'et', label: 'Estonian' },
    { value: 'fil', label: 'Filipino' },
    { value: 'fi', label: 'Finnish' },
    { value: 'fr', label: 'French' },
    { value: 'ka', label: 'Georgian' },
    { value: 'de', label: 'German' },
    { value: 'el', label: 'Greek' },
    { value: 'gu', label: 'Gujarati' },
    { value: 'ha', label: 'Hausa' },
    { value: 'he', label: 'Hebrew' },
    { value: 'hi', label: 'Hindi' },
    { value: 'hu', label: 'Hungarian' },
    { value: 'id', label: 'Indonesian' },
    { value: 'ga', label: 'Irish' },
    { value: 'it', label: 'Italian' },
    { value: 'ja', label: 'Japanese' },
    { value: 'kn', label: 'Kannada' },
    { value: 'kk', label: 'Kazakh' },
    { value: 'ko', label: 'Korean' },
    { value: 'lo', label: 'Lao' },
    { value: 'lv', label: 'Latvian' },
    { value: 'lt', label: 'Lithuanian' },
    { value: 'mk', label: 'Macedonian' },
    { value: 'ms', label: 'Malay' },
    { value: 'ml', label: 'Malayalam' },
    { value: 'mr', label: 'Marathi' },
    { value: 'nb', label: 'Norwegian' },
    { value: 'fa', label: 'Persian' },
    { value: 'pl', label: 'Polish' },
    { value: 'pt_BR', label: 'Portuguese (Brazil)' },
    { value: 'pt_PT', label: 'Portuguese (Portugal)' },
    { value: 'pa', label: 'Punjabi' },
    { value: 'ro', label: 'Romanian' },
    { value: 'ru', label: 'Russian' },
    { value: 'sr', label: 'Serbian' },
    { value: 'sk', label: 'Slovak' },
    { value: 'sl', label: 'Slovenian' },
    { value: 'es', label: 'Spanish' },
    { value: 'es_AR', label: 'Spanish (Argentina)' },
    { value: 'es_ES', label: 'Spanish (Spain)' },
    { value: 'es_MX', label: 'Spanish (Mexico)' },
    { value: 'sw', label: 'Swahili' },
    { value: 'sv', label: 'Swedish' },
    { value: 'ta', label: 'Tamil' },
    { value: 'te', label: 'Telugu' },
    { value: 'th', label: 'Thai' },
    { value: 'tr', label: 'Turkish' },
    { value: 'uk', label: 'Ukrainian' },
    { value: 'ur', label: 'Urdu' },
    { value: 'uz', label: 'Uzbek' },
    { value: 'vi', label: 'Vietnamese' },
    { value: 'zu', label: 'Zulu' },
];

interface FormState {
    name: string;
    category: string;
    language: string;
    headerText: string;
    bodyText: string;
    footerText: string;
}

const defaultForm = (): FormState => ({
    name: '',
    category: 'MARKETING',
    language: 'pt_PT',
    headerText: '',
    bodyText: '',
    footerText: '',
});

const extractTextFromComponents = (components: WhatsAppTemplateComponent[]): Pick<FormState, 'headerText' | 'bodyText' | 'footerText'> => {
    const headerText = components.find(c => c.type === 'HEADER')?.text ?? '';
    const bodyText = components.find(c => c.type === 'BODY')?.text ?? '';
    const footerText = components.find(c => c.type === 'FOOTER')?.text ?? '';
    return { headerText, bodyText, footerText };
};

const buildComponents = (form: FormState): WhatsAppTemplateComponent[] => {
    const components: WhatsAppTemplateComponent[] = [];
    if (form.headerText.trim()) {
        components.push({ type: 'HEADER', format: 'TEXT', text: form.headerText.trim() });
    }
    components.push({ type: 'BODY', text: form.bodyText.trim() });
    if (form.footerText.trim()) {
        components.push({ type: 'FOOTER', text: form.footerText.trim() });
    }
    return components;
};

interface WhatsAppTemplateFormModalProps {
    isOpen: boolean;
    template?: WhatsAppTemplateCatalogItem;
    onClose: () => void;
    onSuccess: () => void;
}

const WhatsAppTemplateFormModal: React.FC<WhatsAppTemplateFormModalProps> = ({
    isOpen,
    template,
    onClose,
    onSuccess,
}) => {
    const t = useTranslations();
    const dispatch = useDispatch();
    const isEdit = !!template;

    const categoryOptions = React.useMemo(() => [
        { value: 'MARKETING', label: t(TK.whatsappTemplateCategoryMarketing) },
        { value: 'UTILITY', label: t(TK.whatsappTemplateCategoryUtility) },
    ], [t]);

    const [form, setForm] = React.useState<FormState>(defaultForm);
    const [saving, setSaving] = React.useState(false);

    React.useEffect(() => {
        if (isOpen) {
            if (template) {
                setForm({
                    name: template.providerTemplateName,
                    category: template.category,
                    language: template.language,
                    ...extractTextFromComponents(template.componentsSnapshot),
                });
            } else {
                setForm(defaultForm());
            }
        }
    }, [isOpen, template]);

    const canSave = form.bodyText.trim().length > 0 && (isEdit || (form.name.trim().length > 0 && form.category.length > 0 && form.language.length > 0));

    const handleSave = React.useCallback(async () => {
        setSaving(true);
        try {
            if (isEdit && template) {
                await updateWhatsAppTemplateAsync(template.providerTemplateId, {
                    components: buildComponents(form),
                });
                dispatch(alertSuccess(t(TK.whatsappTemplateUpdateSuccess)));
            } else {
                await createWhatsAppTemplateAsync({
                    name: form.name.trim(),
                    category: form.category,
                    language: form.language,
                    components: buildComponents(form),
                });
                dispatch(alertSuccess(t(TK.whatsappTemplateCreateSuccess)));
            }
            onSuccess();
            onClose();
        } catch (err: unknown) {
            dispatch(alertError(getErrorMessage(err) || t(TK.somethingWentWrong)));
        } finally {
            setSaving(false);
        }
    }, [isEdit, template, form, dispatch, t, onSuccess, onClose]);

    const set = (key: keyof FormState) => (value: string) => setForm(prev => ({ ...prev, [key]: value }));

    return (
        <Modal isOpen={isOpen} toggle={onClose} size="lg">
            <ModalHeader toggle={onClose}>
                {isEdit ? t(TK.edit) + ' ' + t(TK.whatsappTemplate) : t(TK.whatsappTemplatesCreate)}
            </ModalHeader>
            <ModalBody style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-M)' }}>
                {!isEdit && (
                    <TextInputField
                        label={t(TK.name)}
                        value={form.name}
                        onChange={set('name')}
                        helpText={t(TK.whatsappTemplateNameHint)}
                        required
                    />
                )}
                {isEdit ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-XXS)' }}>
                        <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>{t(TK.category)}</div>
                        <div style={{ color: 'var(--text-body)', fontSize: '0.88rem' }}>
                            {categoryOptions.find(o => o.value === form.category)?.label ?? form.category}
                        </div>
                    </div>
                ) : (
                    <ComboField
                        id="wa-category"
                        label={t(TK.category)}
                        value={form.category}
                        options={categoryOptions}
                        helpText={t(TK.whatsappTemplateCategoryHint)}
                        onChange={v => setForm(prev => ({ ...prev, category: v ?? '' }))}
                    />
                )}
                {!isEdit && (
                    <ComboField
                        id="wa-language"
                        label={t(TK.language)}
                        value={form.language}
                        options={LANGUAGES}
                        onChange={v => setForm(prev => ({ ...prev, language: v ?? '' }))}
                    />
                )}
                <TextInputField
                    label={t(TK.whatsappTemplateHeader) + ' (' + t(TK.optional).toLowerCase() + ')'}
                    value={form.headerText}
                    onChange={set('headerText')}
                />
                <TextInputField
                    label={t(TK.whatsappTemplateBody)}
                    value={form.bodyText}
                    onChange={set('bodyText')}
                    type="textarea"
                    rows={4}
                    helpText={t(TK.whatsappTemplateVariablesHint)}
                    required
                />
                <TextInputField
                    label={t(TK.whatsappTemplateFooter) + ' (' + t(TK.optional).toLowerCase() + ')'}
                    value={form.footerText}
                    onChange={set('footerText')}
                />
            </ModalBody>
            <ModalFooter>
                <AppButton color="secondary" onClick={onClose} disabled={saving}>
                    {t(TK.cancel)}
                </AppButton>
                <AppButton color="primary" onClick={handleSave} disabled={!canSave || saving}>
                    {saving ? <Spinner size="sm" /> : (isEdit ? t(TK.save) : t(TK.create))}
                </AppButton>
            </ModalFooter>
        </Modal>
    );
};

export default WhatsAppTemplateFormModal;
